import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { EventDto } from '@/types/dtos/events/response/event.dto.ts';
import type { UpdateEventDto } from '@/types/dtos/events/request/update-event.dto.ts';
import { GET_EVENT_QUERY_KEY } from '../../queries/events/use-get-event.ts';
import { invalidatePublicationStateQueries } from '@/state/queries/events/use-publication-state';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { useFlowStore } from '@/state/stores/use-flow-store';

export const useUpdateEventMutation = (options?: { onSuccess?: (data: EventDto) => void }) => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateEventDto: UpdateEventDto) => {
      const token = await getToken();
      return updateEvent({ updateEventDto, token: token ?? '' });
    },
    onSuccess: async (data, variables) => {
      const invalidations: Promise<void>[] = [
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_QUERY_KEY],
        }),
        invalidatePublicationStateQueries(queryClient),
      ];

      const titleOrDescriptionChanged = variables.title !== undefined || variables.description !== undefined;

      if (!data.multiFlow && titleOrDescriptionChanged) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
        );

        const patch: Record<string, string | undefined> = {};
        if (variables.title !== undefined) patch.title = variables.title;
        if (variables.description !== undefined) patch.description = variables.description;

        const store = useFlowStore.getState();
        if (store.flowPlan) {
          useFlowStore.setState({ flowPlan: { ...store.flowPlan, ...patch } });
        }
        if (store.flowOrganize) {
          useFlowStore.setState({ flowOrganize: { ...store.flowOrganize, ...patch } });
        }
        if (store.flow) {
          useFlowStore.setState({ flow: { ...store.flow, ...patch } });
        }
      }

      await Promise.all(invalidations);
      options?.onSuccess?.(data);
    },
  });
};

const updateEvent = async ({
  updateEventDto,
  token,
}: {
  updateEventDto: UpdateEventDto;
  token: string;
}): Promise<EventDto> => {
  const url = `${import.meta.env.VITE_API_URL}/events/${updateEventDto.id}`;
  return sendJSONRequest({
    url,
    method: METHOD.PATCH,
    body: updateEventDto,
    token,
  });
};
